import type {GraphQLResponse} from '@github-ui/item-picker-base/api/graphql'
import {useMutation} from '@github-ui/react-query'
import type {UpdateMilestoneDetailsMutationMutation} from '@github-ui/server-persisted-queries/types/updateMilestoneDetailsMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback, useRef} from 'react'

import {LABELS} from './constants/labels'
import {formatFormErrorMessage} from './utils/format-form-error-message'

export type UpdateMilestoneDetailsFormInput = {
  id: string
  title: string
  description?: string | null
  dueOn?: string | null
}

export type UpdatedMilestone = NonNullable<
  NonNullable<UpdateMilestoneDetailsMutationMutation['updateMilestone']>['milestone']
>

// The milestone identity is supplied per call (not at hook init) because the edit page mounts
// this hook before the milestone is guaranteed present; passing it at call time lets the caller
// source it from an already-guarded milestone instead of a meaningless empty/zero default.
export type UpdateMilestoneDetailsVariables = {
  repositoryNameWithOwner: string
  milestoneNumber: number
  input: UpdateMilestoneDetailsFormInput
}

type UseUpdateMilestoneDetailsOptions = {
  setSubmissionErrors: (value: string | null) => void
  onSuccess: (milestone: UpdatedMilestone) => void | Promise<void>
}

export function milestoneEditErrorMessage(error: Error) {
  return error.cause && Array.isArray(error.cause) ? formatFormErrorMessage(error.cause) : LABELS.milestoneEditError
}

// Sends the milestone details write and normalizes transport and payload failures.
// A transport rejection, a non-2xx status, a missing milestone, or a non-empty payload `errors`
// array all surface the generic edit error. Server-provided error messages (the GraphQL envelope
// `errors` array) are threaded through the thrown error's `cause` so the caller can render them
// via `formatFormErrorMessage`.
export async function updateMilestoneDetails(
  repositoryNameWithOwner: string,
  milestoneNumber: number,
  input: UpdateMilestoneDetailsFormInput,
): Promise<UpdateMilestoneDetailsMutationMutation> {
  let response: Response
  try {
    response = await reactFetchJSON(`/${repositoryNameWithOwner}/milestones/${milestoneNumber}`, {
      method: 'PUT',
      body: {
        title: input.title,
        description: input.description,
        dueOn: input.dueOn,
      },
    })
  } catch {
    throw new Error(LABELS.milestoneEditError)
  }

  // Parse the body once (best-effort). A malformed body leaves `result` undefined so it falls
  // through to the generic error rather than leaking a raw parse/TypeError message.
  let result: GraphQLResponse<UpdateMilestoneDetailsMutationMutation> | undefined
  try {
    result = (await response.json()) as GraphQLResponse<UpdateMilestoneDetailsMutationMutation>
  } catch {
    result = undefined
  }

  const errors = result && 'errors' in result ? result.errors : undefined

  // A non-2xx status or a GraphQL envelope `errors` payload surface the generic edit error, but
  // we thread any server-provided messages through `cause` so onError can render them.
  if (!response.ok || (errors && errors.length > 0)) {
    throw errors && errors.length > 0
      ? new Error(LABELS.milestoneEditError, {cause: errors})
      : new Error(LABELS.milestoneEditError)
  }

  // A missing milestone or a non-empty payload `errors` array both surface the generic edit error.
  const payloadErrors = result && 'data' in result ? result.data?.updateMilestone?.errors : undefined
  if (
    !result ||
    !('data' in result) ||
    !result.data?.updateMilestone?.milestone ||
    (payloadErrors && payloadErrors.length > 0)
  ) {
    throw new Error(LABELS.milestoneEditError)
  }

  const milestone = result.data.updateMilestone.milestone
  if (
    typeof milestone.id !== 'string' ||
    typeof milestone.title !== 'string' ||
    typeof milestone.closed !== 'boolean' ||
    typeof milestone.updatedAt !== 'string' ||
    typeof milestone.progressPercentage !== 'number' ||
    (milestone.description !== null && typeof milestone.description !== 'string') ||
    (milestone.descriptionHTML !== null && typeof milestone.descriptionHTML !== 'string') ||
    (milestone.dueOn !== null && typeof milestone.dueOn !== 'string')
  ) {
    throw new Error(LABELS.milestoneEditError)
  }

  return result.data
}

// Updates a milestone's details through the `updateMilestoneDetailsMutation` server-persisted
// query and hands successful writes to the consumer before navigation. There is no optimistic UI.
// Error messaging remains consistent for transport and payload failures.
export function useUpdateMilestoneDetails({setSubmissionErrors, onSuccess}: UseUpdateMilestoneDetailsOptions) {
  const setIsSubmittingRef = useRef<(isSubmitting: boolean) => void>(() => {})

  const {mutate} = useMutation<UpdateMilestoneDetailsMutationMutation, Error, UpdateMilestoneDetailsVariables>({
    mutationFn: ({repositoryNameWithOwner, milestoneNumber, input}: UpdateMilestoneDetailsVariables) =>
      updateMilestoneDetails(repositoryNameWithOwner, milestoneNumber, input),
    onError: (error: Error) => {
      // Server-provided field errors (threaded via `error.cause`) render through
      // formatFormErrorMessage; every other failure — transport error, non-2xx, a
      // missing milestone, or a malformed body — surfaces the generic edit error.
      setSubmissionErrors(milestoneEditErrorMessage(error))
      setIsSubmittingRef.current(false)
    },
    onSuccess: async data => {
      // Reset the submitting state before handing off to the caller. The current consumer
      // navigates away on success (unmounting the form), but resetting here keeps the hook
      // correct for callers that stay mounted.
      setIsSubmittingRef.current(false)
      const updated = data.updateMilestone?.milestone
      if (updated) {
        await onSuccess(updated)
      }
    },
  })

  return useCallback(
    (variables: UpdateMilestoneDetailsVariables, setIsSubmitting: (isSubmitting: boolean) => void) => {
      setIsSubmittingRef.current = setIsSubmitting
      mutate(variables)
    },
    [mutate],
  )
}
